import {
  useDeleteAllNotifications,
  useDeleteNotification,
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useMarkNotificationUnread,
  useNotifications,
  useNotificationStats,
} from "@/application/queries/notifications";
import type { Notification } from "@/domain/notification";
import { fail, succeed, type WorkflowOutcome } from "@/application/workflows/shared/outcome";

export type NotificationReadFilter = "all" | "unread" | "read";

export type NotificationDeletionTarget = Notification | "all";

const READ_FILTER_VALUES: Record<NotificationReadFilter, boolean | undefined> = {
  all: undefined,
  unread: false,
  read: true,
};

function uniqueByUuid(notifications: Notification[]) {
  return [
    ...new Map(notifications.map((notification) => [notification.uuid, notification])).values(),
  ];
}

export function useNotificationInbox(filter: NotificationReadFilter) {
  const query = useNotifications({ is_read: READ_FILTER_VALUES[filter] });
  const stats = useNotificationStats();
  const markRead = useMarkNotificationRead();
  const markUnread = useMarkNotificationUnread();
  const markAll = useMarkAllNotificationsRead();
  const deleteOne = useDeleteNotification();
  const deleteAll = useDeleteAllNotifications();
  const mutations = [markRead, markUnread, markAll, deleteOne, deleteAll];

  const open = async (notification: Notification): Promise<WorkflowOutcome> => {
    try {
      if (!notification.is_read) await markRead.mutateAsync(notification.uuid);
      return succeed(undefined);
    } catch (error) {
      return fail(error);
    }
  };

  const toggleRead = async (notification: Notification): Promise<WorkflowOutcome> => {
    try {
      const mutation = notification.is_read ? markUnread : markRead;
      await mutation.mutateAsync(notification.uuid);
      return succeed(undefined);
    } catch (error) {
      return fail(error);
    }
  };

  const markAllRead = async (): Promise<WorkflowOutcome> => {
    try {
      await markAll.mutateAsync();
      return succeed(undefined);
    } catch (error) {
      return fail(error);
    }
  };

  const remove = async (target: NotificationDeletionTarget): Promise<WorkflowOutcome> => {
    try {
      if (target === "all") await deleteAll.mutateAsync();
      else await deleteOne.mutateAsync(target.uuid);
      return succeed(undefined);
    } catch (error) {
      return fail(error);
    }
  };

  return {
    query,
    stats,
    notifications: uniqueByUuid(query.data?.pages.flatMap((page) => page.notifications) ?? []),
    isBusy: mutations.some((mutation) => mutation.isPending),
    open,
    toggleRead,
    markAllRead,
    remove,
  };
}
